/**
 * WILLS - "Complete your routine" upsell inside the cart drawer.
 *
 * The Liquid snippet (`snippets/cart-routine.liquid`) decides WHICH product to
 * offer. This module only performs the AJAX add and lets the theme's own
 * cart plumbing (cart-items-component / cart-icon / cart-drawer) re-render the
 * drawer section, which swaps the block to the next missing product.
 *
 * It mirrors the add path used by `product-form.js` so the theme reacts exactly
 * as it does for a regular add-to-cart: dispatch a `CartLinesUpdateEvent` with a
 * deferred promise, POST to /cart/add.js requesting the cart sections, then
 * resolve the promise with the fresh cart + sections HTML.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// How long to keep the loading state after the cart promise resolves, so the
// spinner doesn't blink off before the section morph paints the next product.
const SETTLE_DELAY = 350;

class CartRoutineComponent extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-routine-add]'));
    if (!button) return;

    event.preventDefault();
    this.#add(button);
  };

  #showError() {
    const error = this.querySelector('.cart-routine__error');
    if (error instanceof HTMLElement) error.hidden = false;
  }

  #hideError() {
    const error = this.querySelector('.cart-routine__error');
    if (error instanceof HTMLElement) error.hidden = true;
  }

  #settle() {
    setTimeout(() => {
      this.#busy = false;
      this.removeAttribute('data-loading');
      for (const button of this.querySelectorAll('[data-routine-add]')) {
        if (button instanceof HTMLButtonElement) button.disabled = false;
      }
    }, SETTLE_DELAY);
  }

  /** Fetches the live cart so the header bubble gets an accurate total. */
  async #fetchCart() {
    const base = window.Theme?.routes?.cart_url || '/cart';
    const response = await fetch(`${base}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (this.#busy) return;

    const variantId = button.getAttribute('data-routine-add');
    if (!variantId) return;

    this.#busy = true;
    this.#hideError();
    this.setAttribute('data-loading', '');
    button.disabled = true;

    // Refresh every cart section the page currently renders (drawer and/or page).
    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        sectionIds.push(element.dataset.sectionId);
      }
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((result) => result.json());

      if (response.status) {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }

      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          sections: response.sections,
          source: 'cart-routine-component',
          didError: false,
        },
      });

      this.#settle();
    } catch (error) {
      deferred.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      this.#showError();
      this.#settle();
    }
  }
}

if (!customElements.get('cart-routine-component')) {
  customElements.define('cart-routine-component', CartRoutineComponent);
}
